/**
 * The API client, typed from the server's OpenAPI schema (PLAN.md decision 3.7).
 *
 * `schema.d.ts` is generated - never edit it. After an API change:
 *     uv run python scripts/export_openapi.py && (cd web && npm run gen:api)
 * CI regenerates both and fails if the committed copies differ, so a changed endpoint surfaces here
 * as a compile error rather than a runtime bug.
 */

import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";

export type Schemas = components["schemas"];

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;
let currentOrg: string | null = null;

/** Called by sign-in (Phase 4) with a function that returns a fresh ID token. */
export function setTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

/** Act for another organisation the user belongs to; null means their default. */
export function setOrganisation(orgId: string | null): void {
  currentOrg = orgId;
}

const auth: Middleware = {
  async onRequest({ request }) {
    const token = tokenProvider ? await tokenProvider() : null;
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    if (currentOrg) request.headers.set("X-Org-Id", currentOrg);
    return request;
  },
};

/** A file download from an authenticated endpoint: fetch it with the token, then save it. */
export async function downloadFile(path: string, orgHeader = true): Promise<void> {
  const headers = new Headers();
  const token = tokenProvider ? await tokenProvider() : null;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (orgHeader && currentOrg) headers.set("X-Org-Id", currentOrg);
  const response = await fetch(path, { headers });
  if (!response.ok) throw new Error(`the download failed (${response.status})`);
  const name = /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ?? "download";
  saveBlob(await response.blob(), name);
}

/** Hands the browser a file it already has in memory (a CSV or report built in the page). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Same-origin in production (Firebase Hosting rewrites /api to Cloud Run); proxied by Vite in dev. */
export const api = createClient<paths>({ baseUrl: "" });
api.use(auth);
